import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Container, Card, Row, Col, Form, Button } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import Select from "react-select";

function AddGroupMaster() {
  const routeLocation = useLocation();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};

  const [editId, setEditId] = useState(null);

  // dropdown lists
  const [grouptypes, setGrouptype] = useState([]);
  const [subGroupList, setSubGroupList] = useState([]); // Level-1 groups returned by /get_group_master_level
  const [groupNameOptions, setGroupNameOptions] = useState([]); // group_name + group_code

  const [loadingSubGroups, setLoadingSubGroups] = useState(false);
  const [loadingGroupNames, setLoadingGroupNames] = useState(false);

  // selected parent group master id from Sub Group dropdown
  const [parentGroupMasterId, setParentGroupMasterId] = useState("");

  const [form, setForm] = useState({
    group_type: "",
    pl_group_code: "", // Sub Group Name (we store EXACT label returned)
    sub_description: "", // Group Name dropdown (exact)
    sub_code: "", // Group Code (auto from group_name selection)
    last_level: false,
    status: "A",
    school_id: userObj?.school_id || "",
  });

  // -------------------- LOAD GROUP TYPES --------------------
  useEffect(() => {
    loadGroupTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadGroupTypes = async () => {
    try {
      const res = await axios.get(`${baseUrl}/getgrouptypes/`, {
        params: { action: "READ", school_id: userObj.school_id },
      });
      setGrouptype(res.data.group_types || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load Group Types");
    }
  };

  // -------------------- LOAD SUB GROUPS (NO SEARCH) --------------------
  // ✅ Just pass Level and group_type_id (as you asked)
  const loadSubGroups = async (groupTypeId) => {
    if (!groupTypeId) {
      setSubGroupList([]);
      return;
    }

    try {
      setLoadingSubGroups(true);
      const res = await axios.get(`${baseUrl}/get_group_master_level/`, {
        params: {
          Level: 1,
          group_type_id: groupTypeId,
        },
      });

      // expected res.data.Response = [{ group_master_id, group_code: "EXP Expenses", ... }]
      setSubGroupList(res.data.Response || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load Sub Group Name list");
      setSubGroupList([]);
    } finally {
      setLoadingSubGroups(false);
    }
  };

  // -------------------- LOAD GROUP NAMES BY GROUP TYPE --------------------
  const loadGroupNames = async (groupTypeId) => {
    if (!groupTypeId) {
      setGroupNameOptions([]);
      return;
    }

    try {
      setLoadingGroupNames(true);
      const res = await axios.get(`${baseUrl}/finance/subgroup/by-grouptype`, {
        params: { group_type_id: groupTypeId },
      });

      // expected:
      // data: [{ group_name: "Expenses", group_code: "Ex" }]
      setGroupNameOptions(res.data?.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load Group Name dropdown");
      setGroupNameOptions([]);
    } finally {
      setLoadingGroupNames(false);
    }
  };

  // -------------------- EDIT MODE --------------------
  useEffect(() => {
    if (routeLocation.state?.GroupData) {
      const data = routeLocation.state.GroupData;

      setEditId(data.group_master_id);

      setForm({
        group_type: data.group_type_id || "",
        pl_group_code: data.parent_description || "", // ✅ store EXACT "EXP Expenses" or whatever comes
        sub_code: data.group_code || "",
        sub_description: data.description || "",
        last_level: data.levl === 2 ? true : false,
        status: data.status || "A",
        school_id: userObj.school_id,
      });

      setParentGroupMasterId(data.parentgroup_master_id || "");

      // load dropdowns
      if (data.group_type_id) {
        loadSubGroups(data.group_type_id);
        loadGroupNames(data.group_type_id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeLocation]);

  // -------------------- COMMON --------------------
  const checkDuplicateSubGroup = async (subCode, groupMasterId) => {
    if (!subCode) return false;

    try {
      const res = await axios.get(`${baseUrl}/check-duplication-group-master/`, {
        params: { sub_group: subCode, group_master_id: groupMasterId || 0 },
      });

      return res.data === 0;
    } catch (error) {
      toast.error("Error while validating Group Code");
      return false;
    }
  };

  const handleReset = () => {
    setForm({
      group_type: "",
      pl_group_code: "",
      sub_description: "",
      sub_code: "",
      last_level: false,
      status: "A",
      school_id: userObj?.school_id || "",
    });

    setEditId(null);
    setParentGroupMasterId("");
    setSubGroupList([]);
    setGroupNameOptions([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.group_type) return toast.error("Parent Group Type is required");
    if (!parentGroupMasterId) return toast.error("Sub Group Name is required");
    if (!form.sub_description) return toast.error("Group Name is required");
    if (!form.sub_code) return toast.error("Group Code is required");

    const isAllowed = await checkDuplicateSubGroup(form.sub_code, editId);
    if (!isAllowed) return toast.error("Record Already Exists With Same Group Code");

    const payload = {
      group_master_id: editId || 0,
      parentgroup_master_id: parentGroupMasterId,
      group_code: form.sub_code,
      description: form.sub_description,
      group_type_id: form.group_type,
      is_last_level: form.last_level,
      created_by: userObj?.username || "admin",
      action: editId ? "UPDATE" : "CREATE",
      school_id: userObj?.school_id || 0,
    };

    try {
      await axios.post(`${baseUrl}/addupdategroup_master/`, payload, {
        headers: { "Content-Type": "application/json" },
      });
      toast.success(editId ? "Record Updated Successfully" : "Record Added Successfully");
      handleReset();
    } catch (error) {
      console.error(error.response?.data || error.message);
      toast.error(error?.response?.data?.message || "Record already exists");
    }
  };

  // -------------------- SELECTED VALUES --------------------
  const selectedGroupType =
    form.group_type && grouptypes?.length
      ? {
          value: form.group_type,
          label:
            grouptypes.find((g) => String(g.group_type_id) === String(form.group_type))
              ?.group_type_name || "",
        }
      : null;

  const selectedSubGroup =
    parentGroupMasterId && form.pl_group_code
      ? { value: parentGroupMasterId, label: form.pl_group_code }
      : null;

  const selectedGroupName =
    form.sub_description
      ? { value: form.sub_description, label: form.sub_description }
      : null;

  return (
    <div className="pageBody">
      <ToastContainer />
      <Container fluid>
        <Card>
          <Card.Body>
            <form onSubmit={handleSubmit}>
              <Row>
                <Col xs={12}>
                  <h6 className="commonSectionTitle">Add Group Master</h6>
                </Col>
              </Row>

              <Row>
                {/* Parent Group Type */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Parent Group Type <span className="requiredStar">*</span>
                      </Form.Label>

                      <Select
                        isDisabled={!!editId}
                        value={selectedGroupType}
                        onChange={(selected) => {
                          const gtId = selected?.value || "";

                          // reset dependent fields
                          setForm((prev) => ({
                            ...prev,
                            group_type: gtId,
                            pl_group_code: "",
                            sub_description: "",
                            sub_code: "",
                          }));
                          setParentGroupMasterId("");
                          setSubGroupList([]);
                          setGroupNameOptions([]);

                          // ✅ fetch both dropdowns based on group type
                          loadSubGroups(gtId);
                          loadGroupNames(gtId);
                        }}
                        options={(grouptypes || []).map((gt) => ({
                          value: gt.group_type_id,
                          label: gt.group_type_name,
                        }))}
                        placeholder="Select group type"
                        isSearchable
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          menu: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* ✅ Sub Group Name (NOT searchable) */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Group Name <span className="requiredStar">*</span>
                      </Form.Label>

                      <Select
                        isDisabled={!form.group_type || !!editId}
                        isLoading={loadingSubGroups}
                        value={selectedSubGroup}
                        onChange={(selected) => {
                          // ✅ populate as how it gives (EXACT group_code string)
                          const picked = (subGroupList || []).find(
                            (x) => String(x.group_master_id) === String(selected?.value)
                          );

                          setParentGroupMasterId(picked?.group_master_id || "");
                          setForm((prev) => ({
                            ...prev,
                            pl_group_code: picked?.group_code || "",
                          }));
                        }}
                        options={(subGroupList || []).map((item) => ({
                          value: item.group_master_id,
                          label: item.group_code, // EXACT (e.g., "EXP Expenses")
                        }))}
                        placeholder="Select Sub Group"
                        isSearchable={false} // ✅ removed search
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          menu: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* ✅ Group Name (dropdown, NOT searchable) */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Sub Group Name <span className="requiredStar">*</span>
                      </Form.Label>

                      <Select
                        isDisabled={!form.group_type}
                        isLoading={loadingGroupNames}
                        value={selectedGroupName}
                        onChange={(selected) => {
                          const picked = (groupNameOptions || []).find(
                            (x) => String(x.group_name) === String(selected?.value)
                          );

                          setForm((prev) => ({
                            ...prev,
                            sub_description: picked?.group_name || "",
                            sub_code: picked?.group_code || "", // ✅ auto-fill Group Code
                          }));
                        }}
                        options={(groupNameOptions || []).map((g) => ({
                          value: g.group_name,
                          label: g.group_name, // EXACT
                        }))}
                        placeholder="Select Group Name"
                        isSearchable={false} // ✅ removed search
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          menu: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* ✅ Group Code (auto populated, readonly) */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                      Sub Group Code <span className="requiredStar">*</span>
                      </Form.Label>
                      <Form.Control type="text" value={form.sub_code} placeholder="Auto" readOnly />
                    </Form.Group>
                  </div>
                </Col>

                {/* Last Level */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <Form.Group className="mt-2">
                    <Form.Check
                      type="checkbox"
                      id="last_level"
                      label="Last Level"
                      checked={form.last_level}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          last_level: e.target.checked,
                        }))
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-between mt-3">
                <Button type="button" variant="primary" className="btn-info clearBtn" onClick={handleReset}>
                  Reset
                </Button>

                <div>
                  <Button
                    type="button"
                    variant="primary"
                    className="btn-danger secondaryBtn me-2"
                    onClick={() => window.history.back()}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary" className="btn-success primaryBtn">
                    Submit
                  </Button>
                </div>
              </div>
            </form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default AddGroupMaster;
